import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { getBackendUrl } from "../utils/paths";
import Header from "../Components/Header";
import Footer from "../Components/Footer";
import JobApplicationForm from "./JobApplicationForm";
import "./JobDetail.css";

export default function JobDetail() {
  const { id } = useParams();
  const numericId = useMemo(() => {
    if (!id) return null;
    const m = String(id).match(/^(\d+)/);
    return m ? m[1] : id;
  }, [id]);

  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showApplicationForm, setShowApplicationForm] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError("");
      try {
        const res = await fetch(getBackendUrl(`/backend/api/public/careers.php?id=${numericId}`));
        const data = await res.json();
        if (!cancelled) {
          if (!res.ok || !data.item) {
            setError("Job not found.");
          } else {
            setJob(data.item);
          }
          setLoading(false);
        }
      } catch (e) {
        if (!cancelled) {
          setError("Failed to load job.");
          setLoading(false);
        }
      }
    }
    if (numericId) load();
    return () => { cancelled = true; };
  }, [numericId]);

  if (loading) {
    return (
      <>
        <Header />
        <div className="job-detail-container"><p className="loading-text">Loading job details...</p></div>
        <Footer />
      </>
    );
  }

  if (error) {
    return (
      <>
        <Header />
        <div className="job-detail-container"><p className="error-text">{error}</p></div>
        <Footer />
      </>
    );
  }

  if (!job) {
    return (
      <>
        <Header />
        <div className="job-detail-container"><p className="error-text">Job not found.</p></div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      
      {/* Hero Banner */}
      <div className="job-hero-banner">
        <div className="job-hero-bg">
          <div className="job-hero-overlay"></div>
          <div className="job-hero-shapes">
            <div className="job-hero-shape job-hero-shape-1"></div>
            <div className="job-hero-shape job-hero-shape-2"></div>
          </div>
        </div>
        <div className="job-hero-content">
          <div className="job-hero-badge">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2L15.09 8.26H21.77L16.84 12.41L17.97 18.66L12 14.42L6.03 18.66L7.16 12.41L2.23 8.26H8.91L12 2Z" />
            </svg>
            Career Opportunity
          </div>
          <h1 className="job-hero-title">Join Our Mission</h1>
          <p className="job-hero-subtitle">Make a meaningful impact while building your career with Vikash Foundation</p>
        </div>
      </div>

      <div className="job-detail-container">
      <div className="job-detail-wrapper">
        {/* Left Column */}
        <div className="job-detail-main">
          {/* Header Card */}
          <div className="job-header-card">
            <div className="job-header-top">
              {job.logo_path && (
                <img src={`${window.location.origin}${job.logo_path}`} alt="company" className="company-logo" />
              )}
              <div className="job-title-block">
                <h1 className="job-title">{job.title}</h1>
                <p className="company-meta">
                  <span className="meta-badge">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 22s7-7.58 7-12.5A7 7 0 0 0 5 9.5C5 14.42 12 22 12 22Z" />
                      <circle cx="12" cy="9.5" r="2.3" />
                    </svg>
                    {job.office_location}
                  </span>
                  <span className="meta-badge">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="5" width="18" height="16" rx="2" />
                      <path d="M3 9H21M8 3V6M16 3V6" />
                    </svg>
                    Deadline: {job.last_date}
                  </span>
                </p>
              </div>
            </div>
          </div>

          {/* Main Content Cards */}
          <div className="job-content-card">
            <div className="section">
              <h2 className="section-title">Job Description</h2>
              <div className="section-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M9 12h6m-6 4h6m2-11H7a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2z" />
                </svg>
              </div>
              <p className="section-content" style={{ whiteSpace: 'pre-line' }}>
                {job.description}
              </p>
            </div>

            <div className="divider"></div>

            <div className="section">
              <h2 className="section-title">Requirements</h2>
              <div className="requirements-grid">
                <div className="requirement-item">
                  <div className="requirement-icon">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
                    </svg>
                  </div>
                  <div>
                    <p className="requirement-label">Education Qualification</p>
                    <p className="requirement-value">{job.qualification}</p>
                  </div>
                </div>

                <div className="requirement-item">
                  <div className="requirement-icon">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="1" />
                      <path d="M12 8v-2m0 10v2M8.22 3.97l1.42 1.41M14.36 14.22l1.41 1.41M3.97 8.22l1.41 1.42M14.22 14.36l1.41 1.41" />
                      <circle cx="12" cy="12" r="9" />
                    </svg>
                  </div>
                  <div>
                    <p className="requirement-label">Years of Experience</p>
                    <p className="requirement-value">{job.year_of_passing || 'Fresher'}</p>
                  </div>
                </div>

                <div className="requirement-item">
                  <div className="requirement-icon">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                      <polyline points="9 22 9 12 15 12 15 22" />
                    </svg>
                  </div>
                  <div>
                    <p className="requirement-label">Location</p>
                    <p className="requirement-value">{job.office_location}</p>
                  </div>
                </div>

                <div className="requirement-item">
                  <div className="requirement-icon">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                    </svg>
                  </div>
                  <div>
                    <p className="requirement-label">Contact</p>
                    <p className="requirement-value">
                      <a href={`mailto:${job.contact_email}`} className="contact-link">{job.contact_email}</a>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* CTA Button */}
          <div className="job-cta-section">
            <button
              onClick={() => setShowApplicationForm(true)}
              className="vh-donate-btn job-apply-btn"
            >
              Apply Now
            </button>
          </div>
        </div>

        {/* Right Column - Why Join Us */}
        <div className="job-detail-sidebar">
          <div className="sidebar-card join-us-card">
            <div className="join-us-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm3.5-9c.83 0 1.5-.67 1.5-1.5S16.33 8 15.5 8 14 8.67 14 9.5s.67 1.5 1.5 1.5zm-7 0c.83 0 1.5-.67 1.5-1.5S9.33 8 8.5 8 7 8.67 7 9.5 7.67 11 8.5 11zm3.5 6.5c2.33 0 4.31-1.46 5.11-3.5H6.89c.8 2.04 2.78 3.5 5.11 3.5z" />
              </svg>
            </div>
            <h3 className="sidebar-title">Ready to Make an Impact?</h3>
            <p className="sidebar-desc">Join our mission and contribute to a better society.</p>
            <button
              onClick={() => setShowApplicationForm(true)}
              className="vh-donate-btn sidebar-btn"
            >
              Apply Now
            </button>
          </div>

          <div className="sidebar-card why-join-card">
            <h3 className="sidebar-title">Why Join Us?</h3>
            <div className="why-item">
              <div className="why-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 2L15.09 8.26H21.77L16.84 12.41L17.97 18.66L12 14.42L6.03 18.66L7.16 12.41L2.23 8.26H8.91L12 2Z" />
                </svg>
              </div>
              <div>
                <p className="why-title">Make a Difference</p>
                <p className="why-desc">Contribute to meaningful social change</p>
              </div>
            </div>
            <div className="why-item">
              <div className="why-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
                  <polyline points="13 2 13 9 20 9" />
                </svg>
              </div>
              <div>
                <p className="why-title">Grow Your Career</p>
                <p className="why-desc">Learn, grow and build your future with us</p>
              </div>
            </div>
            <div className="why-item">
              <div className="why-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
              </div>
              <div>
                <p className="why-title">Great Culture</p>
                <p className="why-desc">Work with a supportive and inclusive team</p>
              </div>
            </div>
          </div>

          <div className="sidebar-card quote-card">
            <blockquote className="quote-text">
              "Your career can be the reason someone smiles today. Be the change you wish to see in the world."
            </blockquote>
            <div className="quote-icon">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" opacity="0.4">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
            </div>
          </div>
        </div>
      </div>
      </div>
      
      {/* Application Form Modal */}
      {showApplicationForm && (
        <JobApplicationForm
          job={job}
          onClose={() => setShowApplicationForm(false)}
        />
      )}
      
      <Footer />
    </>
  );
}


